// cart.js

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, null, null, true);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');
    const debouncedChange = debounce(e => this.onChange(e), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedChange.bind(this));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
      this.clampQuantities();
    });
    this.clampQuantities();
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
  }

  /**
   * Read the max attribute applied to the input for this variant
   */
  getMetafieldInventory(variantId) {
    const input = this.querySelector(`input[data-quantity-variant-id="${variantId}"]`);
    return parseInt(input?.getAttribute('max'), 10) || 0;
  }

  /**
   * Clamp quantity inputs based on their max attribute
   */
  async clampQuantities() {
    // Determine which inventory suffix to use (usa-inventory or canada-inventory)
    this.dataset.inventorySuffix = window.currentInventorySuffix;
    const suffix = this.dataset.inventorySuffix;
    if (!suffix) return;

    const toClamp = [];
    this.querySelectorAll('[data-quantity-variant-id]').forEach(input => {
      const invMgmt   = input.dataset.inventoryManagement;
      const invPolicy = input.dataset.inventoryPolicy;
      if (invMgmt !== 'shopify' || invPolicy === 'continue') return;

      // Use the max attribute already set in Liquid
      const maxAttr = input.getAttribute('max');
      const max = parseInt(maxAttr, 10);
      if (isNaN(max)) return;

      input.setAttribute('max', max);
      const current = parseInt(input.value, 10) || 0;
      if (current > max) {
        input.value = max;
        toClamp.push({
          line: input.dataset.index,
          qty: max,
          name: input.name,
          variantId: input.dataset.quantityVariantId
        });
      }
    });

    for (const { line, qty, name, variantId } of toClamp) {
      await this.updateQuantity(line, qty, name, variantId, false, true).catch(() => {});
      this.updateLiveRegions(line, window.cartStrings.inventoryRegionLimit.replace('[quantity]', qty));
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.name,
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart-count', '.cart-drawer-shipping-progress', '.bundle-progress-bar']
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector)
            const sourceElement = html.querySelector(selector)
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement)
            }
          }
          this.clampQuantities()
        })
        .catch((e) => {
          console.error(e)
        })
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          const sourceQty = html.querySelector('cart-items')
          this.innerHTML = sourceQty.innerHTML
          this.clampQuantities()
        })
        .catch((e) => {
          console.error(e)
        })
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-bundle-progress',
        section: 'cart-bundle-progress',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ]
  }

  updateQuantity(line, quantity, name, variantId, isRemove = false, wasClamped = false) {
    const input = this.querySelector(`input[data-quantity-variant-id="${variantId}"]`);
    const invMgmt  = input?.dataset.inventoryManagement;
    const invPolicy = input?.dataset.inventoryPolicy;
    if (invMgmt !== 'shopify' || invPolicy === 'continue') {
      // just do a straight update without any clamping
      return this._rawUpdate(line, quantity, name, variantId)
      .catch(()=>{});
    }
    // parse and validate quantity
    const maxStock = this.getMetafieldInventory(variantId)
    const parsed = parseInt(quantity, 10)
    if (isNaN(parsed)) {
      return
    }
    let qtyToUse = parsed
    let partial = false

    if (wasClamped) {
      qtyToUse = quantity;
      partial = true;
    } else {
      if (isRemove) {
        qtyToUse = 0;
      } else if (qtyToUse > maxStock) {
        qtyToUse = maxStock;
        partial = true;
      }
    }

    // immediate UI update
    const qEl = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`)
    if (qEl) qEl.value = qtyToUse

    // AJAX update
    this.enableLoading(line)
    const body = JSON.stringify({ line, quantity: qtyToUse, sections: this.getSectionsToRender().map(s => s.section), sections_url: window.location.pathname })
    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then(r => r.text())
      .then(state => {
        const parsedState = JSON.parse(state)
        if (parsedState.errors) {
          this.updateLiveRegions(line, parsedState.errors);
          return Promise.reject(parsedState.errors);
        }
        // empty state toggles
        this.classList.toggle('is-empty', parsedState.item_count === 0)
        const drawer = document.querySelector('cart-drawer')
        const footer = document.getElementById('main-cart-footer')
        if (footer) footer.classList.toggle('is-empty', parsedState.item_count === 0)
        if (drawer) drawer.classList.toggle('is-empty', parsedState.item_count === 0)
        // re-render sections
        this.getSectionsToRender().forEach(sec => {
          const tgt = document.getElementById(sec.id).querySelector(sec.selector) || document.getElementById(sec.id)
          tgt.innerHTML = this.getSectionInnerHTML(parsedState.sections[sec.section], sec.selector)
        })
        if (partial) {
          // use the appropriate singular/plural message
          const message = qtyToUse === 1
            ? window.cartStrings.inventoryUpdatedOne.replace('[quantity]', qtyToUse)
            : window.cartStrings.inventoryUpdatedOther.replace('[quantity]', qtyToUse);
          
          this.updateLiveRegions(line, message);
        }
        // focus - but not when there's a partial add (inventory limit)
        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`)
        if (!isRemove && !partial && lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          drawer
            ? trapFocus(drawer, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus()
        }
        document.dispatchEvent(new CustomEvent('cart:ui:updated'))
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });

        return Promise.resolve();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach(o => o.classList.add('hidden'))
        const errs = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors')
        if (errs) errs.textContent = window.cartStrings.error;
      })
      .finally(() => this.disableLoading(line))
  }

  _rawUpdate(line, quantity, name, variantId) {
    this.enableLoading(line)

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    })

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text()
      })
      .then((state) => {
        const parsedState = JSON.parse(state)
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`)
        const items = document.querySelectorAll('.cart-item')

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value')
          this.updateLiveRegions(line, parsedState.errors)
          return
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0)
        const cartDrawerWrapper = document.querySelector('cart-drawer')
        const cartFooter = document.getElementById('main-cart-footer')

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0)
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0)

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id)
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          )
        })
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined
        let message = ''
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue)
          }
        }
        this.updateLiveRegions(line, message)

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`)
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus()
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper.querySelector('a')
          )
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }

        const event = new CustomEvent('cart:ui:updated');
        document.dispatchEvent(event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        })
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden')
        )
        const errors =
          document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors')
        errors.textContent = window.cartStrings.error
      })
      .finally(() => {
        this.disableLoading(line)
      })
  }

  showLineError(line, message) {
    // try both main‑cart and drawer IDs
    console.log('Attempting to show error for line', line, 'with message:', message);

    const ids = [`Line-item-error-${line}`, `CartDrawer-LineItemError-${line}`];
    for (const id of ids) {
      const container = document.getElementById(id);
      if (!container) continue;
  
      const textEl = container.querySelector('.cart-item__error-text');
      if (textEl) textEl.textContent = message;
  
      // remove any CSS that hides it
      container.classList.remove('hidden', 'visually-hidden', 'hidden-error');
  
      // auto‑hide after 3s
      setTimeout(() => container.classList.add('hidden'), 3000);
      return; // we found and updated one container—done
    }
    console.warn(`Could not find error container for line ${line}`);
  }  

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`)
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message

    this.lineItemStatusElement.setAttribute('aria-hidden', true)

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText')
    cartStatus.setAttribute('aria-hidden', false)

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true)
    }, 1000)
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems')
    mainCartItems.classList.add('cart__items--disabled')

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`)
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    )

    ;[...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    )

    document.activeElement.blur()
    this.lineItemStatusElement.setAttribute('aria-hidden', false)
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems')
    mainCartItems.classList.remove('cart__items--disabled')

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`)
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    )

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'))
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'))
  }
}

customElements.define('cart-items', CartItems)

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super()

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value })
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          }, ON_CHANGE_DEBOUNCE_TIMER)
        )
      }
    }
  )
}